import { Search } from 'lucide-react'
import pageStyles from '@/pages/DeploymentsPage.module.css'
import type { DeploymentStatus, Environment } from '@/types'
import type { ProvisioningStatus, ServerMode } from '@/types/workwise'
import {
  ENVIRONMENTS,
  type FilterChip,
  type FilterState,
  MODE_OPTIONS,
  PROVISIONING_OPTIONS,
  STATUSES,
  chipStatus,
} from './filters'

const CHIPS: FilterChip[] = ['all', 'active', 'completed', 'failed']

interface DeploymentsFiltersProps {
  filters: FilterState
  chip: FilterChip
  /** Top-level products for the product filter (slug + name). */
  products: Array<{ slug: string; name: string }>
  onChange: (patch: Partial<FilterState>) => void
  onChipChange: (chip: FilterChip) => void
}

/** Filter chips and the filter bar. Every change resets the page (done by the page). */
export function DeploymentsFilters({ filters, chip, products, onChange, onChipChange }: DeploymentsFiltersProps) {
  const effectiveStatus = chipStatus(chip, filters.status)
  return (
    <>
      <div className={pageStyles.filterChips}>
        {CHIPS.map((c) => (
          <button
            key={c}
            className={`${pageStyles.chip} ${chip === c ? pageStyles.chipActive : ''}`}
            data-testid={`chip-${c}`}
            onClick={() => onChipChange(c)}
          >
            {c.charAt(0).toUpperCase() + c.slice(1)}
          </button>
        ))}
      </div>

      <div className={pageStyles.filterBar}>
        <div className={pageStyles.searchWrapper}>
          <Search size={15} className={pageStyles.searchIcon} />
          <input
            type="text"
            placeholder="Search deployments..."
            value={filters.search}
            onChange={(e) => onChange({ search: e.target.value })}
            className={pageStyles.searchInput}
          />
        </div>
        <input
          type="text"
          placeholder="Customer or number"
          aria-label="Customer"
          data-testid="filter-customer"
          value={filters.customer}
          onChange={(e) => onChange({ customer: e.target.value })}
          className={pageStyles.select}
          style={{ backgroundImage: 'none', cursor: 'text', width: 200 }}
        />
        <select
          aria-label="Product"
          data-testid="filter-product"
          value={filters.product}
          onChange={(e) => onChange({ product: e.target.value })}
          className={pageStyles.select}
        >
          <option value="">All Products</option>
          {products.map((p) => (
            <option key={p.slug} value={p.slug}>
              {p.name}
            </option>
          ))}
        </select>
        <select
          aria-label="Environment"
          data-testid="filter-environment"
          value={filters.environment}
          onChange={(e) => onChange({ environment: e.target.value as Environment | '' })}
          className={pageStyles.select}
        >
          {ENVIRONMENTS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Status"
          data-testid="filter-status"
          value={chip === 'all' ? filters.status : effectiveStatus}
          onChange={(e) => {
            onChange({ status: e.target.value as DeploymentStatus | '' })
            onChipChange('all')
          }}
          className={pageStyles.select}
          disabled={chip !== 'all'}
        >
          {STATUSES.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Provisioning status"
          data-testid="filter-provisioning"
          value={filters.provisioning}
          onChange={(e) => onChange({ provisioning: e.target.value as ProvisioningStatus | '' })}
          className={pageStyles.select}
        >
          {PROVISIONING_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          aria-label="DB mode"
          data-testid="filter-db-mode"
          value={filters.dbMode}
          onChange={(e) => onChange({ dbMode: e.target.value as ServerMode | '' })}
          className={pageStyles.select}
        >
          {MODE_OPTIONS('All DB modes').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
        <select
          aria-label="Gateway mode"
          data-testid="filter-gateway-mode"
          value={filters.gatewayMode}
          onChange={(e) => onChange({ gatewayMode: e.target.value as ServerMode | '' })}
          className={pageStyles.select}
        >
          {MODE_OPTIONS('All gateway modes').map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>
    </>
  )
}
